import { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Layers, Star, AlertTriangle, History, Package } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const fmtCur = (n) =>
  n === null || n === undefined || n === ''
    ? '—'
    : `₹${new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0)}`;

const fmtMargin = (n) => {
  if (n === null || n === undefined || n === '') return '—';
  const v = Number(n);
  const sign = v > 0 ? '+' : v < 0 ? '−' : '';
  return `${sign}${fmtCur(Math.abs(v))}`;
};

const todayStr = () => new Date().toISOString().slice(0, 10);
const fmtDate = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d) ? String(s).slice(0, 10) : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

function AllotPlatingModal({ part, suppliers, editQuote, onClose, onSaved }) {
  const isEdit = !!editQuote?.plating_costing_id;
  const [form, setForm] = useState(
    isEdit
      ? { supplier_id: editQuote.supplier_id, plating_cost: editQuote.plating_cost ?? '', notes: editQuote.notes || '',
          effective_date: (editQuote.effective_date || '').slice(0, 10) || todayStr(), change_reason: '' }
      : { supplier_id: '', plating_cost: '', notes: '', effective_date: todayStr(), change_reason: '' }
  );
  const [saving, setSaving] = useState(false);

  // When adding, hide suppliers that already have a price for this part.
  const usedIds = new Set((part.quotes || []).map(q => q.supplier_id));
  const selectable = isEdit ? suppliers : suppliers.filter(s => !usedIds.has(s.supplier_id));

  const priceChanged = isEdit && Number(form.plating_cost) !== Number(editQuote.plating_cost);

  const handleSave = async () => {
    if (!isEdit && !form.supplier_id) { toast.error('Choose a supplier'); return; }
    if (form.plating_cost === '' || isNaN(Number(form.plating_cost))) { toast.error('Enter a plating price'); return; }
    if (!form.effective_date) { toast.error('Pick an effective date'); return; }
    if (priceChanged && !form.change_reason.trim()) { toast.error('Add a reason for the price revision'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/plating-costings/${editQuote.plating_costing_id}`, {
          plating_cost: Number(form.plating_cost),
          effective_date: form.effective_date,
          change_reason: priceChanged ? form.change_reason.trim() : undefined,
          notes: form.notes,
        });
        toast.success(priceChanged ? 'Plating price revised — history retained' : 'Plating price updated');
      } else {
        await api.post('/plating-costings', {
          part_id: part.part_id,
          supplier_id: form.supplier_id,
          plating_cost: Number(form.plating_cost),
          effective_date: form.effective_date,
          notes: form.notes,
        });
        toast.success('Supplier plating price added');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-slate-900">
            {isEdit ? 'Edit' : 'Allot'} Plating Supplier
          </DialogTitle>
        </DialogHeader>
        <div className="mt-1 mb-3 text-xs text-slate-500">
          <span className="font-mono font-semibold text-slate-700">{part.part_number}</span> — {part.part_name}
          <span className="block mt-0.5">Part plating cost: <span className="font-mono">{fmtCur(part.part_plating_cost)}</span></span>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Supplier *</label>
            {isEdit ? (
              <div className="w-full border border-slate-200 bg-slate-50 rounded-sm h-9 px-3 text-sm flex items-center text-slate-700">
                {editQuote.supplier_name}
              </div>
            ) : (
              <select value={form.supplier_id} onChange={e => setForm(f => ({ ...f, supplier_id: e.target.value }))}
                data-testid="plating-supplier-select"
                className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900">
                <option value="">Select a supplier…</option>
                {selectable.map(s => <option key={s.supplier_id} value={s.supplier_id}>{s.name}</option>)}
              </select>
            )}
            {!isEdit && selectable.length === 0 && (
              <p className="text-[11px] text-amber-600 mt-1">All suppliers are already allotted to this part.</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Plating Price (₹/pc) *</label>
              <input type="number" step="0.01" value={form.plating_cost}
                onChange={e => setForm(f => ({ ...f, plating_cost: e.target.value }))}
                data-testid="plating-cost-input"
                className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-slate-900" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Effective Date *</label>
              <input type="date" value={form.effective_date}
                onChange={e => setForm(f => ({ ...f, effective_date: e.target.value }))}
                data-testid="plating-effective-date"
                className="w-full border border-slate-300 rounded-sm h-9 px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-slate-900" />
            </div>
          </div>
          {isEdit && priceChanged && (
            <div className="rounded-sm border border-amber-200 bg-amber-50 p-2.5">
              <label className="text-xs font-bold uppercase text-amber-700 mb-1 block">Revision Reason *</label>
              <input type="text" value={form.change_reason}
                onChange={e => setForm(f => ({ ...f, change_reason: e.target.value }))}
                placeholder="e.g. Q2 price revision, zinc rate change"
                data-testid="plating-change-reason"
                className="w-full border border-amber-300 rounded-sm h-9 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-amber-500" />
              <p className="text-[10px] text-amber-600 mt-1">
                Was {fmtCur(editQuote.plating_cost)} · the previous price is kept in history.
              </p>
            </div>
          )}
          <div>
            <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Notes</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2}
              className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-900" />
          </div>
          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 h-9 border border-slate-300 rounded-sm text-slate-700 text-sm font-semibold hover:bg-slate-50">Cancel</button>
            <button onClick={handleSave} disabled={saving || (!isEdit && selectable.length === 0)} data-testid="save-plating-btn"
              className="flex-1 h-9 bg-slate-900 text-white text-sm font-bold uppercase rounded-sm hover:bg-slate-800 disabled:opacity-50">
              {saving ? 'Saving…' : (isEdit ? (priceChanged ? 'Revise' : 'Update') : 'Allot')}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PlatingHistoryModal({ quote, part, onClose }) {
  const [history, setHistory] = useState(null);
  useEffect(() => {
    api.get(`/plating-costings/${quote.plating_costing_id}/history`)
      .then(r => setHistory(r.data || []))
      .catch(() => setHistory([]));
  }, [quote.plating_costing_id]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-slate-900">Plating Price History</DialogTitle>
        </DialogHeader>
        <div className="mt-1 mb-3 text-xs text-slate-500">
          <span className="font-mono font-semibold text-slate-700">{part.part_number}</span> — {quote.supplier_name}
        </div>
        {history === null ? (
          <div className="py-8 text-center text-slate-400 text-sm">Loading…</div>
        ) : history.length === 0 ? (
          <div className="py-8 text-center text-slate-400 text-sm">No history.</div>
        ) : (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="plating-history-list">
            {history.map((h, i) => (
              <div key={i} className={`flex items-start justify-between gap-3 p-2.5 rounded-sm border ${
                i === 0 ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
                <div>
                  <p className="font-mono text-sm font-bold text-slate-900">
                    {fmtCur(h.price)}
                    {i === 0 && <span className="ml-2 text-[10px] font-sans font-semibold uppercase text-emerald-600">Current</span>}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{h.reason}</p>
                  {h.updated_by && <p className="text-[10px] text-slate-400 mt-0.5">by {h.updated_by}</p>}
                </div>
                <div className="text-right whitespace-nowrap">
                  <p className="text-xs font-semibold text-slate-600">Eff. {fmtDate(h.effective_date)}</p>
                  {h.changed_at && <p className="text-[10px] text-slate-400 mt-0.5">set {fmtDate(h.changed_at)}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function PlatingComparo() {
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('all'); // 'all' | 'unassigned' | supplier_id
  const [allotPart, setAllotPart] = useState(null);
  const [editQuote, setEditQuote] = useState(null); // { quote, part }
  const [historyQuote, setHistoryQuote] = useState(null); // { quote, part }

  const fetchData = async () => {
    try {
      const [comparo, sups] = await Promise.all([
        api.get('/plating-comparo'),
        api.get('/suppliers'),
      ]);
      setRows(comparo.data || []);
      setSuppliers(sups.data || []);
    } catch { toast.error('Failed to load plating data'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleDeleteQuote = async (q) => {
    if (!window.confirm(`Remove ${q.supplier_name}'s plating price?`)) return;
    try {
      await api.delete(`/plating-costings/${q.plating_costing_id}`);
      toast.success('Plating price removed');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove'); }
  };

  // Suppliers that currently have at least one plating quote → their own tabs.
  const supplierTabs = useMemo(() => {
    const map = new Map();
    rows.forEach(r => (r.quotes || []).forEach(q => {
      if (!map.has(q.supplier_id)) map.set(q.supplier_id, { supplier_id: q.supplier_id, name: q.supplier_name, count: 0 });
      map.get(q.supplier_id).count += 1;
    }));
    return [...map.values()].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [rows]);

  const unassignedCount = rows.filter(r => r.supplier_count === 0).length;

  const visibleRows = useMemo(() => {
    let list = rows;
    if (tab === 'unassigned') list = rows.filter(r => r.supplier_count === 0);
    else if (tab !== 'all') list = rows.filter(r => (r.quotes || []).some(q => q.supplier_id === tab));
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(r =>
        r.part_number?.toLowerCase().includes(s) ||
        r.part_name?.toLowerCase().includes(s) ||
        r.customer_name?.toLowerCase().includes(s));
    }
    return list;
  }, [rows, tab, search]);

  const TabBtn = ({ id, label, count, tone }) => (
    <button onClick={() => setTab(id)} data-testid={`plating-tab-${id}`}
      className={`px-3.5 py-2 text-sm font-semibold rounded-sm whitespace-nowrap transition-colors border ${
        tab === id ? 'bg-slate-900 text-white border-slate-900'
                   : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
      {label}
      {count !== undefined && (
        <span className={`ml-1.5 text-[11px] font-mono ${
          tab === id ? 'text-slate-300' : tone === 'warn' && count > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
          {count}
        </span>
      )}
    </button>
  );

  return (
    <div className="space-y-5" data-testid="plating-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-bold text-slate-900 text-xl flex items-center gap-2">
            <Layers size={20} className="text-slate-500" /> Plating Suppliers
          </h2>
          <p className="text-slate-500 text-sm mt-0.5">
            {rows.length} part{rows.length !== 1 ? 's' : ''} with plating
            {unassignedCount > 0 && <span className="text-amber-600"> · {unassignedCount} unassigned</span>}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-sm p-3">
        <Search size={14} className="text-slate-400" />
        <input type="text" placeholder="Search parts…" value={search} onChange={e => setSearch(e.target.value)}
          data-testid="plating-search"
          className="border-0 outline-none text-sm flex-1 bg-transparent placeholder:text-slate-400" />
      </div>

      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <TabBtn id="all" label="All Parts" count={rows.length} />
        <TabBtn id="unassigned" label="Unassigned" count={unassignedCount} tone="warn" />
        {supplierTabs.length > 0 && <div className="w-px h-6 bg-slate-200 mx-1 shrink-0" />}
        {supplierTabs.map(s => <TabBtn key={s.supplier_id} id={s.supplier_id} label={s.name} count={s.count} />)}
      </div>

      {loading ? (
        <div className="p-12 text-center text-slate-400">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto mb-3"></div>
          Loading plating data…
        </div>
      ) : visibleRows.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm p-12 text-center text-slate-400">
          {rows.length === 0
            ? 'No parts have a plating cost yet.'
            : tab === 'unassigned' ? 'No unassigned plating parts — every part has a supplier.'
            : 'No parts match.'}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-auto max-h-[70vh]">
          <table className="w-full text-sm sticky-head">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <th className="text-left font-semibold px-4 py-2.5">Part #</th>
                <th className="text-left font-semibold px-4 py-2.5">Part Name</th>
                <th className="text-left font-semibold px-4 py-2.5">Customer</th>
                <th className="text-right font-semibold px-4 py-2.5">Part Plating</th>
                <th className="text-left font-semibold px-4 py-2.5">Supplier Plating Price</th>
                <th className="text-right font-semibold px-4 py-2.5">Plating Margin</th>
                <th className="text-right font-semibold px-4 py-2.5">Add</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(r => {
                // In a supplier tab, surface that supplier's quote first.
                const quotes = tab !== 'all' && tab !== 'unassigned'
                  ? [...(r.quotes || [])].sort((a, b) => (a.supplier_id === tab ? -1 : b.supplier_id === tab ? 1 : 0))
                  : (r.quotes || []);
                return (
                  <tr key={r.part_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 align-top"
                    data-testid={`plating-row-${r.part_id}`}>
                    <td className="px-4 py-3 font-mono text-slate-800 whitespace-nowrap">{r.part_number}</td>
                    <td className="px-4 py-3 text-slate-700">{r.part_name}</td>
                    <td className="px-4 py-3 text-slate-500">{r.customer_name}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-600 whitespace-nowrap">{fmtCur(r.part_plating_cost)}</td>
                    <td className="px-4 py-3">
                      {quotes.length === 0 ? (
                        <span className="inline-flex items-center gap-1.5 text-xs text-amber-600 font-medium">
                          <AlertTriangle size={13} /> No supplier plating cost set
                        </span>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {quotes.map(q => q.source === 'subcontract' ? (
                            <span key={q.costing_id}
                              className={`inline-flex items-center gap-1.5 pl-2 pr-2 py-1 rounded-sm border text-xs ${
                                q.is_best ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                                          : 'bg-indigo-50 border-indigo-200 text-indigo-800'}`}
                              title={`From whole-part subcontract PO${q.po_number ? ` ${q.po_number}` : ''}. Edit it in the supplier costing.${q.notes ? `\n${q.notes}` : ''}`}>
                              {q.is_best && <Star size={11} className="text-emerald-600 fill-emerald-500" />}
                              <Package size={11} className="text-indigo-500" />
                              <span className="font-medium">{q.supplier_name}</span>
                              <span className="font-mono font-semibold">{fmtCur(q.plating_cost)}</span>
                              <span className="text-[9px] uppercase font-bold tracking-wide text-indigo-500 bg-indigo-100 rounded px-1 py-px">subcontract</span>
                              <span className="text-[10px] text-slate-400 font-normal whitespace-nowrap">
                                w.e.f. {fmtDate(q.effective_date)}
                              </span>
                            </span>
                          ) : (
                            <span key={q.plating_costing_id}
                              className={`group inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-sm border text-xs ${
                                q.is_best ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                                          : 'bg-slate-50 border-slate-200 text-slate-700'}`}
                              title={q.notes || ''}>
                              {q.is_best && <Star size={11} className="text-emerald-600 fill-emerald-500" />}
                              <span className="font-medium">{q.supplier_name}</span>
                              <span className="font-mono font-semibold">{fmtCur(q.plating_cost)}</span>
                              <span className="text-[9px] uppercase font-bold tracking-wide text-slate-400 bg-slate-100 rounded px-1 py-px">jobwork</span>
                              <span className="text-[10px] text-slate-400 font-normal whitespace-nowrap">
                                w.e.f. {fmtDate(q.effective_date)}
                              </span>
                              <button onClick={() => setHistoryQuote({ quote: q, part: r })}
                                title={`Price history${q.revision_count > 1 ? ` (${q.revision_count} entries)` : ''}`}
                                className="relative p-0.5 text-slate-400 hover:text-slate-800 rounded-sm">
                                <History size={11} />
                                {q.revision_count > 1 && (
                                  <span className="absolute -top-1 -right-1 text-[8px] font-bold text-slate-500">{q.revision_count}</span>
                                )}
                              </button>
                              <button onClick={() => setEditQuote({ quote: q, part: r })} title="Revise / Edit"
                                className="p-0.5 text-slate-400 hover:text-slate-800 rounded-sm">
                                <Edit size={11} />
                              </button>
                              <button onClick={() => handleDeleteQuote(q)} title="Remove"
                                className="p-0.5 text-slate-400 hover:text-red-600 rounded-sm">
                                <Trash2 size={11} />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono whitespace-nowrap">
                      {r.plating_margin === null || r.plating_margin === undefined ? (
                        <span className="text-slate-300">—</span>
                      ) : (
                        <span className={
                          Number(r.plating_margin) > 0 ? 'text-emerald-600 font-semibold'
                          : Number(r.plating_margin) < 0 ? 'text-red-600 font-semibold'
                          : 'text-slate-500'}
                          title="What we charge the customer minus what we pay the cheapest plating supplier">
                          {fmtMargin(r.plating_margin)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setAllotPart(r)} data-testid={`allot-plating-${r.part_id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-slate-300 rounded-sm text-slate-700 text-xs font-semibold hover:bg-slate-100 whitespace-nowrap">
                        <Plus size={13} /> Supplier
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {allotPart && (
        <AllotPlatingModal part={allotPart} suppliers={suppliers}
          onClose={() => setAllotPart(null)} onSaved={fetchData} />
      )}
      {editQuote && (
        <AllotPlatingModal part={editQuote.part} suppliers={suppliers} editQuote={editQuote.quote}
          onClose={() => setEditQuote(null)} onSaved={fetchData} />
      )}
      {historyQuote && (
        <PlatingHistoryModal quote={historyQuote.quote} part={historyQuote.part}
          onClose={() => setHistoryQuote(null)} />
      )}
    </div>
  );
}
